<template>
    <TabsRoot
        v-model="activeTab"
        :default-value="defaultValue"
        :orientation="orientation"
        :class="['tabsRoot', orientation === 'vertical' ? 'vertical' : 'horizontal']"
        :unmount-on-hide="unmountOnHide">
        <TabsList v-if="!hideSingleTabList || tabIds.length > 1" :class="['tabsList', listClass]">
            <TabsIndicator :class="['tabsIndicator', indicatorClass]">
                <div class="tabsIndicatorBar" />
            </TabsIndicator>
            <TabsTrigger v-for="tabId of tabIds" :key="tabId" ref="tabTriggers" :class="['tabsTrigger', triggerClass]" :data-tab-id="tabId" :value="tabId" :disabled="disabled">
                {{ labels[tabId] }}
            </TabsTrigger>
        </TabsList>
        <TabsContent v-for="tabId of tabIds" :key="tabId" :value="tabId" :class="['tabsContent', contentClass]">
            <slot :name="`content-${tabId}`"></slot>
        </TabsContent>
    </TabsRoot>
</template>

<script setup lang="ts">
/**
 * Tabs with a text-only label per tab and a content slot per tabId.
 * ie. if you supply tabIds = ['insight', 'memories']
 * and labels = { insight: "Insight", memories: "Memories" }
 * then you supply slots like:
 *   <template #content-insight>...</template>
 *   <template #content-memories>...</template>
 *
 * Labels are plain strings rather than slots so tab triggers can't carry icons.
 */
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "reka-ui";
import { computed, useTemplateRef } from "vue";

type BaseTabsProps = {
    tabIds: string[];
    labels: Record<string, string>;
    hideSingleTabList?: boolean;
    listClass?: string;
    indicatorClass?: string;
    triggerClass?: string;
    contentClass?: string;
    orientation?: "horizontal" | "vertical";
    disabled?: boolean;
    unmountOnHide?: boolean;
};

const {
    tabIds,
    labels,
    hideSingleTabList = false,
    listClass = "",
    indicatorClass = "",
    triggerClass = "",
    contentClass = "",
    orientation = "horizontal",
    disabled = false,
    unmountOnHide = true,
} = defineProps<BaseTabsProps>();

const activeTab = defineModel<string>();
const defaultValue = computed(() => tabIds[0]);
const tabTriggers = useTemplateRef("tabTriggers");

const focusTab = (tabId: string, scrollOptions: ScrollIntoViewOptions = { behavior: "smooth", block: "start", inline: "nearest" }) => {
    const trigger = tabTriggers.value?.find((tabTrigger) => tabTrigger?.$el.dataset.tabId === tabId)?.$el;
    if (!(trigger instanceof HTMLElement)) {
        return;
    }

    trigger.focus({ preventScroll: true });
    trigger.scrollIntoView(scrollOptions);
};

defineExpose({ focusTab });
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .tabsRoot {
        @apply flex;
    }

    .tabsList {
        /* shrink-0: the tab header must never be flex-shrunk by a constrained
           flex-column parent. Shrinking compresses each trigger below its
           natural height, and with border-box that eats the trigger's bottom
           padding — pulling the active-tab underline up against the text
           (visibly so for labels with descenders). */
        @apply relative flex shrink-0;
    }

    .tabsIndicator {
        @apply absolute rounded-none isolate transition-[width,transform,translate] duration-300;
    }

    .tabsIndicatorBar {
        @apply bg-tabs-indicator w-full h-full;
    }

    .tabsTrigger {
        @apply body-small py-2 cursor-pointer pointer-events-auto transition-colors focus-visible:relative data-[state=active]:body-small-bold data-[state=active]:text-tabs-active-text data-[state=inactive]:enabled:hover:text-tabs-active-text disabled:text-tabs-disabled-text disabled:cursor-not-allowed;
    }

    .tabsContent {
        @apply w-full h-full focus-visible:outline-hidden;
    }

    .tabsRoot.horizontal {
        @apply flex-col;
        > .tabsList {
            @apply w-full gap-4 overflow-x-auto overflow-y-clip;
            /* An inset shadow rather than a border so the hairline never adds
               to the list's height; weight is a token because the tonal spec
               draws it thinner than the legacy 2px. */
            box-shadow: 0 calc(-1 * var(--tabs-hairline-weight)) 0 var(--color-tabs-hairline) inset;

            > .tabsIndicator {
                @apply left-0 h-0.5 bottom-0 w-(--reka-tabs-indicator-size) translate-x-(--reka-tabs-indicator-position);
            }
            > .tabsTrigger {
                @apply flex-none pb-3;
            }
        }
    }

    .tabsRoot.vertical {
        @apply flex-row gap-2;
        > .tabsList {
            @apply flex-col gap-1 w-auto overflow-y-auto overflow-x-clip;
            /* Same weight token as the horizontal hairline so both
               orientations thin together under the flag. */
            box-shadow: calc(-1 * var(--tabs-hairline-weight)) 0 0 var(--color-tabs-hairline) inset;

            > .tabsIndicator {
                @apply right-0 top-0 w-0.5 h-(--reka-tabs-indicator-size) translate-y-(--reka-tabs-indicator-position);
            }
            > .tabsTrigger {
                @apply whitespace-nowrap px-4;
            }
        }
    }
}
</style>
